<template>
    <button
        v-if="isLogged"
        :class="customCss"
        @click="toggleRecord"
        :disabled="disabledRecordButton"
    >
        <Icon family="fa" name="fa-circle" />
        <span>{{ this.isRecording ? recordLabel : $t("notification-record-modal.go-record") }}</span>
    </button>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { RepositoryFactory } from "@/services/api";

export default {
    name: "record-button",
    props: {
        initRecording : {type: Boolean, required: false, default: false}
    },
    data() {
        return {
            stream: null,
            recorder: null,
            recordingInterval: null,
            shareStream: null,
            loading: false,
        };
    },
    computed: {
        ...mapGetters("oidcStore", ["oidcIsAuthenticated"]),
        ...mapGetters("conference", [
            "getMediaStreamConstraints",
            "getConferenceId",
            "getParticipants",
            "getRecorder",
            "getShareStream",
            "getStream",
            "isRecording"
        ]),
        customCss() {
            return this.isRecording ? 'button is-danger mr-3' : 'button is-success mr-3'
        },
        isLogged() {
            return this.oidcIsAuthenticated;
        },
        enabledRecord() {
            return this.isLogged;
        },
        recordLabel() {
            return this.isRecording ?
                this.$t("record.stop-record") :
                this.$t("record.record");
        },
        disabledRecordButton() {
            if (this.isRecording) {
                return false;
            }

            if (this.loading || this.getParticipants.length === 0) {
                return true;
            }

            return false;
        },
    },
    async beforeDestroy() {
        await this.disableScreenSharing();
    },
    methods: {
        ...mapActions("conference", [
            "setRecording", "setRecorder", "setShareStream", "setStream"
        ]),
        async toggleRecord() {
            this.loading = true;
            if (!this.isRecording) {
                await this.enableScreenSharing();
            } else {
                await this.disableScreenSharing();
            }
            this.loading = false;
            this.setRecording();
            
            this.$emit("record", this.isRecording);
        },
        setDataRecorder(object){
            this.setRecorder(object);
        },
        setDataShareStream(object){
            this.setShareStream(object);
        },
        setDataStream(object){
            this.setStream(object);
        },
        async enableScreenSharing() {
            try {
                let desktopStream =
                    await navigator.mediaDevices.getDisplayMedia({
                        video: { frameRate: 30 },
                        audio: true,
                    });
                let audioConstraints = Object.assign(
                    {},
                    this.getMediaStreamConstraints
                );
                delete audioConstraints.video;
                let voiceStream = await navigator.mediaDevices.getUserMedia(
                    audioConstraints
                );
                const tracks = [
                    ...desktopStream.getVideoTracks(),
                    ...this.mergeAudioStreams(desktopStream, voiceStream),
                ];
                
                this.stream = new MediaStream(tracks);
                this.setDataStream(this.stream);

                this.shareStream = desktopStream;
                this.setDataShareStream(this.shareStream);
                
                this.enableScreenRecording();
            } catch {
                // this.recording = !this.recording;
                // this.$emit("record", !this.recording);
                this.$emit("record", false);
            }
        },
        async disableScreenSharing() {
            if (this.stream) {
                this.stream.getTracks().forEach(function (track) {
                    track.stop();
                });
            }
            if (this.shareStream !== null) {
                this.shareStream.getTracks().forEach(function (track) {
                    track.stop();
                });
            }
            this.disableScreenRecording();
        },
        enableScreenRecording() {
            this.recorder = new MediaRecorder(this.stream, {
                mimeType: "video/x-matroska;codecs=avc1,opus",
                videoBitsPerSecond: 2500000,
            });

            this.recorder.ondataavailable = this.dataAvailable;
            this.recorder.onstop = this.stopedRecorder;

            this.recorder.start();

            this.recordingInterval = setInterval(
                this.restartRecordProcess,
                5000
            );

            this.setDataRecorder(this.recorder);
        },
        restartRecordProcess() {
            if (this.recorder !== null) {
                this.recorder.stop();
                this.recorder.start();
            }
        },
        dataAvailable(event) {
            this.generateAndSendCrop(event.data);
        },
        stopedRecorder() {
            // this.generateAndSendCrop();
        },
        async generateAndSendCrop(blob) {
            var formData = new FormData();
            formData.append("video", blob, new Date().getTime());
            formData.append("roomId", this.getConferenceId);
            await RepositoryFactory.files.uploadVideo(formData);
        },
        disableScreenRecording() {
            if (this.recorder === null) {
                return;
            }
            this.recorder.stop();
            this.recorder = null;
            this.setDataRecorder(this.recorder);
            clearInterval(this.recordingInterval);
        },
        mergeAudioStreams(desktopStream, voiceStream) {
            const context = new AudioContext();
            const destination = context.createMediaStreamDestination();
            let hasDesktop = false;
            let hasVoice = false;
            if (desktopStream && desktopStream.getAudioTracks().length > 0) {
                const source1 = context.createMediaStreamSource(desktopStream);
                const desktopGain = context.createGain();
                desktopGain.gain.value = 0.7;
                source1.connect(desktopGain).connect(destination);
                hasDesktop = true;
            }

            if (voiceStream && voiceStream.getAudioTracks().length > 0) {
                const source2 = context.createMediaStreamSource(voiceStream);
                const voiceGain = context.createGain();
                voiceGain.gain.value = 0.7;
                source2.connect(voiceGain).connect(destination);
                hasVoice = true;
            }

            let participants = this.getParticipants;
            for (const participant of participants) {
                let tracks = [];
                participant.audioTracks.forEach((v) => {
                    tracks.push(v.track.mediaStreamTrack);
                });

                var stream = new MediaStream(tracks);

                const source = context.createMediaStreamSource(stream);
                const voiceGain = context.createGain();
                voiceGain.gain.value = 0.7;
                source.connect(voiceGain).connect(destination);
                hasVoice = true;
            }

            return hasDesktop || hasVoice
                ? destination.stream.getAudioTracks()
                : [];
        },
    },
    watch: {
        getRecorder: {
            handler(){
                this.recorder = this.getRecorder;
            }
        },
        getShareStream: {
            handler(){
                this.shareStream = this.getShareStream;
            }
        },
        getStream: {
            handler(){
                this.stream = this.getStream;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
    .flex-centered-content {
        display: flex;
        justify-content: center;
    }
</style>